import { useState } from 'react';
import {
  Card,
  CardBody,
  Input,
  Badge,
  Alert,
  Button,
  ButtonGroup,
  FormGroup,
  Label,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import {
  useNexusJobs,
  useNexusJobDetail,
  useCancelNexusJob,
  useRetryNexusJob,
} from './useNexusJobs';

const STATUS_COLORS = {
  pending: 'secondary',
  running: 'info',
  completed: 'success',
  failed: 'danger',
  cancelled: 'warning',
};
const STATUSES = ['', 'pending', 'running', 'completed', 'failed', 'cancelled'];

/**
 * Nexus Jobs-Monitor (Spec 4 von 8).
 *
 * Spec: lmm-wiki/wiki/orbit-specs/nexus-jobs-monitor.md
 * Endpoints: /v1/jobs (Operator-Bearer via BFF-Bearer-Proxy)
 *
 * Auto-Refresh 15s. Cancel-Button für laufende, Retry für failed.
 */
export function NexusJobsPage() {
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [subscriptionId, setSubscriptionId] = useState('');
  const [openId, setOpenId] = useState(null);
  const canWrite = useHasPermission({ perms: ['integrations.write'] });

  const query = useNexusJobs({ status, type, subscriptionId });
  const jobs = query.data?.jobs ?? [];

  return (
    <div>
      <PageHeader
        title="Nexus · Jobs Monitor"
        subtitle={`${jobs.length} Jobs · Auto-Refresh 15s · /v1/jobs`}
      />

      <Card className="mb-3">
        <CardBody className="py-2">
          <div className="d-flex flex-wrap gap-3 align-items-end">
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">Status</Label>
              <Input type="select" bsSize="sm" value={status} onChange={(e) => setStatus(e.target.value)} style={{ minWidth: 150 }}>
                {STATUSES.map((s) => (
                  <option key={s || 'all'} value={s}>{s || 'Alle'}</option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">Type</Label>
              <Input type="text" bsSize="sm" value={type} onChange={(e) => setType(e.target.value)} placeholder="z.B. domain.provision" style={{ minWidth: 200 }} />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">Subscription-ID</Label>
              <Input type="text" bsSize="sm" value={subscriptionId} onChange={(e) => setSubscriptionId(e.target.value)} placeholder="z.B. 17" style={{ minWidth: 140 }} />
            </FormGroup>
            {(status || type || subscriptionId) && (
              <Button size="sm" color="secondary" outline onClick={() => { setStatus(''); setType(''); setSubscriptionId(''); }}>
                Filter zurücksetzen
              </Button>
            )}
          </div>
        </CardBody>
      </Card>

      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!query.isLoading && jobs.length === 0}
        emptyMessage="Keine Jobs für die aktuellen Filter."
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 130 }}>Job-ID</th>
            <th>Type</th>
            <th style={{ width: 110 }}>Status</th>
            <th style={{ width: 110 }}>Schritt</th>
            <th style={{ width: 90 }}>Sub-ID</th>
            <th style={{ width: 130 }}>Erstellt</th>
            <th style={{ width: 130 }}>Beendet</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((j) => (
            <tr key={j.id} role="button" onClick={() => setOpenId(j.id)} style={{ cursor: 'pointer' }}>
              <td><code className="small">{j.id}</code></td>
              <td><code className="small">{j.type}</code></td>
              <td>
                <Badge color={STATUS_COLORS[j.status] ?? 'secondary'} pill>{j.status}</Badge>
              </td>
              <td className="small">
                {j.current_step ?? 0} / {j.total_steps ?? '—'}
              </td>
              <td className="small">{j.subscription_id ?? <span className="text-muted">—</span>}</td>
              <td>{j.created_at ? <RelativeTime iso={j.created_at} /> : <span className="text-muted">—</span>}</td>
              <td>{j.finished_at ? <RelativeTime iso={j.finished_at} /> : <span className="text-muted">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </DataTable>

      <JobDetailDrawer
        jobId={openId}
        isOpen={!!openId}
        onClose={() => setOpenId(null)}
        canWrite={canWrite}
      />
    </div>
  );
}

function JobDetailDrawer({ jobId, isOpen, onClose, canWrite }) {
  const detail = useNexusJobDetail(jobId);
  const cancel = useCancelNexusJob();
  const retry = useRetryNexusJob();
  const job = detail.data;

  const onCancel = async () => {
    await cancel.mutateAsync({ id: jobId });
    onClose();
  };
  const onRetry = async () => {
    await retry.mutateAsync({ id: jobId });
    onClose();
  };

  return (
    <Offcanvas isOpen={isOpen} toggle={onClose} direction="end" style={{ width: 600 }}>
      <OffcanvasHeader toggle={onClose}>
        Job-Detail
      </OffcanvasHeader>
      <OffcanvasBody>
        {detail.isLoading && <p>Lade…</p>}
        {detail.isError && (
          <Alert color="danger">Job konnte nicht geladen werden: {detail.error?.message}</Alert>
        )}
        {job && (
          <>
            <h5 className="mb-3">
              <code>{job.id}</code>{' '}
              <Badge color={STATUS_COLORS[job.status] ?? 'secondary'} pill>{job.status}</Badge>
            </h5>
            <dl className="row small">
              <dt className="col-4 text-muted">Type</dt>
              <dd className="col-8"><code>{job.type}</code></dd>
              <dt className="col-4 text-muted">Subscription</dt>
              <dd className="col-8">{job.subscription_id ?? '—'}</dd>
              <dt className="col-4 text-muted">Schritt</dt>
              <dd className="col-8">{job.current_step ?? 0} / {job.total_steps ?? '—'}</dd>
              <dt className="col-4 text-muted">Erstellt</dt>
              <dd className="col-8">{job.created_at ? <RelativeTime iso={job.created_at} /> : '—'}</dd>
              <dt className="col-4 text-muted">Beendet</dt>
              <dd className="col-8">{job.finished_at ? <RelativeTime iso={job.finished_at} /> : '—'}</dd>
            </dl>

            <h6 className="text-uppercase small text-muted mt-3">Payload</h6>
            <pre className="bg-body-tertiary p-2 small font-monospace" style={{ borderRadius: 6, maxHeight: 200, overflow: 'auto' }}>
              {JSON.stringify(job.payload ?? {}, null, 2)}
            </pre>

            {job.result && (
              <>
                <h6 className="text-uppercase small text-muted mt-3">Result</h6>
                <pre className="bg-body-tertiary p-2 small font-monospace" style={{ borderRadius: 6, maxHeight: 200, overflow: 'auto' }}>
                  {JSON.stringify(job.result, null, 2)}
                </pre>
              </>
            )}

            <HasPermission perms={['integrations.write']}>
              {canWrite && (job.status === 'pending' || job.status === 'running') && (
                <ButtonGroup className="mt-3">
                  <Button color="warning" outline onClick={onCancel} disabled={cancel.isPending}>
                    {cancel.isPending ? 'Cancel läuft…' : '⛔ Cancel'}
                  </Button>
                </ButtonGroup>
              )}
              {canWrite && job.status === 'failed' && (
                <ButtonGroup className="mt-3">
                  <Button color="info" outline onClick={onRetry} disabled={retry.isPending}>
                    {retry.isPending ? 'Retry läuft…' : '🔁 Retry (neuer Job)'}
                  </Button>
                </ButtonGroup>
              )}
            </HasPermission>
          </>
        )}
      </OffcanvasBody>
    </Offcanvas>
  );
}
